/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { storeInstance } from '../store/store.unit';
import { emptySessionAttrs } from './session-attrs';
import sessionResource from './session-resource';

/**
 * @import DeviceStateful from '../../device/device-stateful'
 */

// TODO #409: Správně by se mělo jmenovat PersistentSession, ale po zpětnou kompatibilitu
// starý název (a hlavně jméno resource) zachován.
/**
 * Perzistentní model sezení s nastavením zařízení.
 * Obsahuje hodnoty, které jsou jedinečné pro aktuálně spuštěnou aplikaci.
 * Aktuálně existuje vždy právě jedna instance, protože v aplikaci nedochází k žádnému přepínání mezi sezeními.
 *
 * Typy u volitelných položek předpokládají, že došlo k migraci po spuštění aplikace.
 * @template [Attrs = import('./session-attrs').SessionAttrs]
 * @typedef {Attrs
 *   & import('../stringable').Stringable // Metoda toString v session-resource.js.
 * } Session
 */

// TODO: Nyní pro robustnost ošetřeno, že pokud by {create,save}Session
// rejectnulo, tak by spadla spouštěcí sekvence v Bottle, přišla výjimka
// na Sentry, hrálo by černo (nebo čekám na první obsah?) a zařízení by
// bylo nepoužitelné do doby továrního resetu nebo smazání všech dat.
//
// Nebylo by z hlediska customer experience lepší mít custom name chyby
// pro rozlišení takového stavu volajícím a v tomto případě zobrazit
// fallback dialog, který by jednak informoval o chybě a umožňoval
// smazat vše postradatelné z localStorage (tj. vše kromě klíčů
// `player-lighter/session,{/default}`) a tím umožnil opětovné
// spárování? Takto když případné chyby z pohledu zákazníka/kolemjdoucího
// tiše spadnou (chyby sice přijdou na Sentry, ale budeme o nich vědět
// jenom my), tak se zákazníkovi může zdát, že je vše v pořádku...
/**
 * Obnoví defaultní Session z localStorage a provede případnou "migraci"
 * defaultizací (tj. aby se prakticky zabránilo výskytu undefined volitelných
 * hodnot a non-nullish povinných hodnot v kódu nově přidané klíče). Pokud
 * Session nebyla uložena, pak se vytvoří a vrátí nová s defaultními hodnotami.
 *
 * Případná perzistentní migrace z legacy formátu na ContentSettings JSON se
 * děje až v {@link DeviceStateful.onHeartbeatFetched}, kde se spoléhá, že defaultizace
 * volitelných a povinných hodnot proběhla již zde.
 *
 * Z hlediska robustnosti se musí tato funkce vždy vyřešit a nikdy neodmítnout
 * (aby se aplikace spustila a mohla se případně připojit online pro možnost
 * ruční nápravné akce na základě chyby na Sentry) a vrátit nějakou použitelnou
 * Session (byť neperzistentně vytvořenou/uloženou).
 * @param {import('../logger/logger').Logger} logger
 * @param {import('../store/store').default} store
 * @returns {Promise<Session>}
 */
export async function alwaysEnsureSession(logger, store) {
  /** @type {import('./legacy-session-attrs').LegacySession | undefined} */
  let maybeSession;
  try {
    maybeSession = await store.findSession(emptySessionAttrs.id);
    // Jen a pouze v případě, že Session nebyla nalezena (čisté zařízení),
    // vytvoříme novou prázdnou (a namigrovanou/defaultizovanou) Session.
    if (!maybeSession) {
      return await store.createSession(emptySessionAttrs);
    }
  } catch (error) {
    // Bohužel čtení nebo perzistentní uložení se nezdařilo, tak alespoň vrátíme
    // neperzistentně vytvořenou empty session, aby se zařízení spustilo
    // a mohlo případně proběhnout párování podle (doufejme deterministicky)
    // generovaného fingerprintu.
    // TODO: Zaobalit chybu pro smysluplnou hlášku např. pomocí cause:
    // - https://stackoverflow.com/questions/42754270/re-throwing-exception-in-nodejs-and-not-losing-stack-trace/42755876#42755876
    logger.error(error);
    // Sice je storeInstance primárně určené pro test, ale zde se výjimečně může použít.
    return storeInstance(store, sessionResource.name, emptySessionAttrs);
  }

  /** @type {Session} */
  let session = Object.assign(
    maybeSession,
    // TODO: Nějak zefektivnit, aby se zbytečně nemodifikovalo
    // a taky nemohlo dojít k překlepu.
    /** @satisfies {import('./session-attrs').SessionAttrs} */ ({
      buildId: maybeSession.buildId ?? emptySessionAttrs.buildId,
      deviceId: maybeSession.deviceId ?? emptySessionAttrs.deviceId,
      fingerprint: maybeSession.fingerprint ?? emptySessionAttrs.fingerprint,
      id: maybeSession.id,
      serverUrl: maybeSession.serverUrl ?? emptySessionAttrs.serverUrl,
      storage: maybeSession.storage ?? emptySessionAttrs.storage,
      version: maybeSession.version ?? emptySessionAttrs.version
    })
  );

  if (store.hasChangesSession(session.id)) {
    // Update/migrace Session i v adaptéru.
    try {
      session = await store.saveSession(session.id);
    } catch (error) {
      // Bohužel migrace se nezdařila, ale alespoň vrátíme neperzistentně
      // namigrovanou session, aby se zařízení spustilo a mohla proběhnout
      // nějaká nápravná akce na základě chyby na Sentry.
      // TODO: Zaobalit chybu pro smysluplnou hlášku např. pomocí cause:
      // - https://stackoverflow.com/questions/42754270/re-throwing-exception-in-nodejs-and-not-losing-stack-trace/42755876#42755876
      logger.error(error);
    }
  }
  return session;
}
